'use client';

import { useLayoutEffect, useRef, useState } from 'react';

interface ThemeAttrs {
  'data-color-scheme'?: string;
  'data-aesthetic'?: string;
}

/**
 * Detects the nearest `data-color-scheme` and `data-aesthetic` ancestors
 * so portal-based components can forward them to their portaled content.
 *
 * Returns a probe ref (place on any element inside the theme scope)
 * and a `themeAttrs` object to spread onto the portal popup element.
 */
export function useInheritedColorScheme() {
  const probeRef = useRef<HTMLSpanElement>(null);
  const [themeAttrs, setThemeAttrs] = useState<ThemeAttrs>({});

  useLayoutEffect(() => {
    const el = probeRef.current;
    if (!el) return;

    const attrs: ThemeAttrs = {};

    const schemeEl = el.closest('[data-color-scheme]');
    const schemeValue = schemeEl?.getAttribute('data-color-scheme');
    if (schemeValue !== null && schemeValue !== undefined) {
      attrs['data-color-scheme'] = schemeValue;
    }

    const aestheticEl = el.closest('[data-aesthetic]');
    const aestheticValue = aestheticEl?.getAttribute('data-aesthetic');
    if (aestheticValue !== null && aestheticValue !== undefined) {
      attrs['data-aesthetic'] = aestheticValue;
    }

    setThemeAttrs(attrs);
  }, []);

  return { probeRef, themeAttrs };
}
